import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupWrapper } from '../Filters.style';
import { FilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import ShowAllButton from './ShowAllButton';
import {
  ModelFamilyStyleFrame,
  AllModelsWrapper,
  Legend,
  CTAWrapper,
  CommonModelsTitle,
} from './ModelFamilySelector.style';
import Search from './Search/Search';
import ActiveModelFamiliesList from './ActiveModelFamilies';
import CheckboxSelector from '../../CheckboxComponent';
import CommonModelsList from './CommonModels';
import { CommonModelFamiliesValues } from './modelFamiliesConfigs';
import filterKnownModelFamilies from '../../../utils/filterKnownModelFamilies';
import FilterSection from '../FilterSection';

interface ModelFamilySelectorTypes {
  filterType: string;
  modelFamilyOptions: {
    [key: string]: FilterOption;
  };
  confirmedFilters: FilterTypes;
  availableFilters: string[];
  openedFiltersList: ExposedFiltersTypes;
  handleAddFilter: (filterType: string, value: string, isChecked?: boolean) => void;
  handleExposeFilter: (arg: string) => void;
}

const ModelFamilySelector: React.FC<ModelFamilySelectorTypes> = ({
  filterType,
  modelFamilyOptions,
  confirmedFilters,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
  availableFilters,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  // Animation
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAll]);

  const selectedModelFamilyLabels = useMemo(() => {
    return filterKnownModelFamilies(confirmedFilters.modelFamily, modelFamilyOptions)
      .map((body) => modelFamilyOptions[body].label)
      .join(', ');
  }, [modelFamilyOptions, confirmedFilters.modelFamily]);

  const selectedModelFamilyFilters = useMemo(() => {
    return confirmedFilters.modelFamily;
  }, [confirmedFilters.modelFamily]);

  const groupedModelFamilies = useMemo(() => {
    // Filter options with defined tabs
    const allOptions = Object.values(modelFamilyOptions);

    // Group options by tabs using reduce
    const groupedByTabs = allOptions.reduce(
      (acc, item) => {
        item.tabs?.forEach((tab) => {
          acc[tab] = acc[tab] || [];
          acc[tab].push(item);
        });
        return acc;
      },
      {} as { [key: string]: FilterOption[] },
    );

    return groupedByTabs;
  }, [modelFamilyOptions]);

  const commmonModelFamilyOptions = useMemo(() => {
    const commonModelFamiliesSet = new Set(CommonModelFamiliesValues);

    const filteredOptions = Object.fromEntries(
      Object.entries(modelFamilyOptions).filter(([, value]) =>
        commonModelFamiliesSet.has(value.value),
      ),
    );

    // Soriting the object, so it is foloowing initial order from array
    const sortedOptions = Object.fromEntries(
      Object.entries(filteredOptions).sort(([keyA], [keyB]) => {
        return CommonModelFamiliesValues.indexOf(keyA) - CommonModelFamiliesValues.indexOf(keyB);
      }),
    );

    return sortedOptions;
  }, [modelFamilyOptions]);

  const handleAddModelFamily = (newModelFamily: string, isChecked?: boolean) => {
    handleAddFilter(filterType, newModelFamily, isChecked);
  };

  const isAlpha = !process.env.IS_LEGACY;
  const hasAnyFilterSelected = !!selectedModelFamilyFilters.length;

  return (
    <FilterSection
      filterType={filterType}
      selectedFilterLabels={selectedModelFamilyLabels}
      openedFiltersList={openedFiltersList}
      handleExposeFilter={handleExposeFilter}
    >
      <ModelFamilyStyleFrame>
        <Search
          allOptions={Object.keys(modelFamilyOptions)}
          modelFamilyOptions={modelFamilyOptions}
          onChange={handleAddModelFamily}
        />
        <ActiveModelFamiliesList
          modelFamilyOptions={modelFamilyOptions}
          selectedModelFamilyFilters={selectedModelFamilyFilters}
          onChange={handleAddModelFamily}
        />
        {!isAlpha && (
          <>
            <CommonModelsTitle hasAnyFilterSelected={hasAnyFilterSelected}>
              {t(`promo.filters.filterOptions.modelFamily.modelsNote`)}
            </CommonModelsTitle>
            <CommonModelsList
              commonModelFamilyOptions={commmonModelFamilyOptions}
              selectedModelFamilyFilters={selectedModelFamilyFilters}
              availableFilters={availableFilters}
              onChange={handleAddModelFamily}
              filterType={filterType}
            />
          </>
        )}
        <CTAWrapper expanded={showAll} hasMarginTop={selectedModelFamilyFilters?.length > 0}>
          <ShowAllButton
            showAll={showAll}
            label={t(`promo.filters.filterOptions.modelFamily.showAll`)}
            fewerLabel={t(`promo.filters.filterOptions.modelFamily.showFewer`)}
            onClick={() => setShowAll(!showAll)}
          />
        </CTAWrapper>
        <AllModelsWrapper
          expanded={showAll}
          ref={contentRef}
          contentHeight={`${contentHeight || 0}px`}
        >
          {/* Temporary fix */}
          {showAll && (
            <>
              {Object.keys(groupedModelFamilies).map((group) => {
                return (
                  <div key={group}>
                    <Legend>{t(`promo.filters.filterOptions.modelFamily.${group}`)}</Legend>
                    <GroupWrapper>
                      {groupedModelFamilies[group].map((model) => {
                        return (
                          <CheckboxSelector
                            inputName={`${group}-${model.value}`}
                            key={model.value}
                            onChange={() =>
                              handleAddModelFamily(
                                model.value,
                                selectedModelFamilyFilters.includes(model.value),
                              )
                            }
                            value={model.value}
                            label={model.label}
                            ariaLabel={model.ariaLabel}
                            checked={selectedModelFamilyFilters.includes(model.value)}
                            disabled={!availableFilters.includes(model.value)}
                            filterType={filterType}
                          />
                        );
                      })}
                    </GroupWrapper>
                  </div>
                );
              })}
            </>
          )}
        </AllModelsWrapper>
      </ModelFamilyStyleFrame>
    </FilterSection>
  );
};

export default ModelFamilySelector;
